import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { FileUpload } from 'primereact/fileupload';
import { SignatureAssetService } from 'src/service/SignatureAssetService';
import dayjs from 'dayjs';

export interface SignatureDisplayConfigData {
    displayConfigId?: string;
    showLabel?: string;
    labelText?: string;
    showOwner?: string;
    ownerNameText?: string;
    showAddress?: string;
    emailText?: string;
    showCertDetails?: string;
    showPosition?: string;
    positionText?: string;
    showCompany?: string;
    companyNameText?: string;
    showSignTime?: string;
    showLogo?: string;
    layoutStyle?: string;
    logoContentLocation?: string;
}

interface Props {
    previewImageBase64?: string;
    userName?: string;
    config: SignatureDisplayConfigData;
    onChange: (config: SignatureDisplayConfigData) => void;
}

export const SignatureDisplayConfig: React.FC<Props> = ({ previewImageBase64, userName, config, onChange }) => {
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const logoUploadRef = useRef<FileUpload>(null);

    const handleChange = (field: keyof SignatureDisplayConfigData, value: string) => {
        onChange({ ...config, [field]: value });
    };

    const getLogoUrl = (location: string | undefined) => {
        if (!location) return '';
        if (location.startsWith('data:image')) return location;
        // Proxy URL cho logo
        return `/api/rest/s1/iam-signature-assets/display-config/logo-resource?logoContentLocation=${encodeURIComponent(location)}`;
    };

    const toggle = (field: keyof SignatureDisplayConfigData, checked: boolean) => {
        handleChange(field, checked ? 'Y' : 'N');
    };

    // Chuẩn bị dữ liệu hiển thị preview
    const mockedDisplayLines = [];
    const displayName = config.ownerNameText || userName || 'Tên người ký';
    
    if (config.showLabel === 'Y' && config.labelText) {
        mockedDisplayLines.push(`${config.labelText} ${config.showOwner === 'Y' ? displayName : ''}`);
    } else if (config.showOwner === 'Y') {
        mockedDisplayLines.push(displayName);
    }
    
    if (config.showPosition === 'Y') {
        mockedDisplayLines.push(config.positionText || 'Chức vụ: Giám đốc (Dữ liệu mẫu)');
    }
    if (config.showCompany === 'Y') {
        mockedDisplayLines.push(config.companyNameText || 'Công ty: TNHH Phần Mềm (Dữ liệu mẫu)');
    }
    if (config.showAddress === 'Y') {
        mockedDisplayLines.push(config.emailText || 'Email: example@mail.com (Dữ liệu mẫu)');
    }
    if (config.showCertDetails === 'Y') {
        mockedDisplayLines.push('Người ký: (Tên certificate PKI)');
    }
    if (config.showSignTime === 'Y') {
        mockedDisplayLines.push(`Ngày ký: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`);
    }

    return (
        <div className="grid">
            <div className="col-12 md:col-6">
                <div className="flex flex-column gap-4 pr-3 pl-4 py-3 border-right-1 surface-border">
                    <h5 className="m-0 mb-2 text-lg font-semibold">Tùy chọn hiển thị thông tin</h5>
                    
                    {/* Bố cục */}
                    <div className="flex flex-column gap-2 mb-3">
                        <span className="font-medium text-color-secondary text-sm">Kiểu bố cục</span>
                        <div className="flex gap-4">
                            <div className="flex align-items-center">
                                <RadioButton inputId="style1" name="layout" value="STYLE_1" onChange={(e) => handleChange('layoutStyle', e.value)} checked={config.layoutStyle === 'STYLE_1'} />
                                <label htmlFor="style1" className="ml-2">Phía dưới chữ ký</label>
                            </div>
                            <div className="flex align-items-center">
                                <RadioButton inputId="style2" name="layout" value="STYLE_2" onChange={(e) => handleChange('layoutStyle', e.value)} checked={config.layoutStyle === 'STYLE_2'} />
                                <label htmlFor="style2" className="ml-2">Bên phải chữ ký</label>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-column gap-4">
                        {/* Nhãn */}
                        <div className="flex align-items-center gap-3">
                            <label className="text-sm font-medium white-space-nowrap" style={{ width: '130px' }}>Hiển thị nhãn</label>
                            <div className="flex-1">
                                {config.showLabel === 'Y' && (
                                    <InputText className="p-inputtext-sm w-full" placeholder="VD: Ký bởi:..." value={config.labelText || ''} onChange={(e) => handleChange('labelText', e.target.value)} />
                                )}
                            </div>
                            <InputSwitch checked={config.showLabel === 'Y'} onChange={(e) => toggle('showLabel', e.value || false)} />
                        </div>
                        
                        {/* Tên */}
                        <div className="flex align-items-center gap-3">
                            <label className="text-sm font-medium white-space-nowrap" style={{ width: '130px' }}>Tên người ký</label>
                            <div className="flex-1">
                                {config.showOwner === 'Y' && (
                                    <InputText className="p-inputtext-sm w-full" placeholder="Nhập tên hiển thị..." value={config.ownerNameText || ''} onChange={(e) => handleChange('ownerNameText', e.target.value)} />
                                )}
                            </div>
                            <InputSwitch checked={config.showOwner === 'Y'} onChange={(e) => toggle('showOwner', e.value || false)} />
                        </div>

                        {/* Chức vụ */}
                        <div className="flex align-items-center gap-3">
                            <label className="text-sm font-medium white-space-nowrap" style={{ width: '130px' }}>Chức vụ</label>
                            <div className="flex-1">
                                {config.showPosition === 'Y' && (
                                    <InputText className="p-inputtext-sm w-full" placeholder="Nhập chức vụ..." value={config.positionText || ''} onChange={(e) => handleChange('positionText', e.target.value)} />
                                )}
                            </div>
                            <InputSwitch checked={config.showPosition === 'Y'} onChange={(e) => toggle('showPosition', e.value || false)} />
                        </div>

                        {/* Công ty */}
                        <div className="flex align-items-center gap-3">
                            <label className="text-sm font-medium white-space-nowrap" style={{ width: '130px' }}>Công ty</label>
                            <div className="flex-1">
                                {config.showCompany === 'Y' && (
                                    <InputText className="p-inputtext-sm w-full" placeholder="Nhập tên công ty..." value={config.companyNameText || ''} onChange={(e) => handleChange('companyNameText', e.target.value)} />
                                )}
                            </div>
                            <InputSwitch checked={config.showCompany === 'Y'} onChange={(e) => toggle('showCompany', e.value || false)} />
                        </div>

                        {/* Email */}
                        <div className="flex align-items-center gap-3">
                            <label className="text-sm font-medium white-space-nowrap" style={{ width: '130px' }}>Địa chỉ email</label>
                            <div className="flex-1">
                                {config.showAddress === 'Y' && (
                                    <InputText className="p-inputtext-sm w-full" placeholder="Nhập email..." value={config.emailText || ''} onChange={(e) => handleChange('emailText', e.target.value)} />
                                )}
                            </div>
                            <InputSwitch checked={config.showAddress === 'Y'} onChange={(e) => toggle('showAddress', e.value || false)} />
                        </div>

                        {/* Các trường khác */}
                        <div className="flex align-items-center justify-content-between">
                            <label className="text-sm font-medium">Thông tin chứng thư (PKI)</label>
                            <InputSwitch checked={config.showCertDetails === 'Y'} onChange={(e) => toggle('showCertDetails', e.value || false)} />
                        </div>
                        {/* Logo */}
                        <div className="flex flex-column gap-3">
                            <div className="flex align-items-center justify-content-between">
                                <label className="text-sm font-medium">Hiển thị Logo tổ chức</label>
                                <InputSwitch checked={config.showLogo === 'Y'} onChange={(e) => toggle('showLogo', e.value || false)} />
                            </div>
                            {config.showLogo === 'Y' && (
                                <div className="flex align-items-center gap-3 pl-4">
                                    <FileUpload 
                                        ref={logoUploadRef}
                                        mode="basic" 
                                        name="logo" 
                                        accept="image/png,image/jpeg" 
                                        maxFileSize={500000} 
                                        onSelect={async (e) => {
                                            const file = e.files[0];
                                            if (file) {
                                                try {
                                                    setUploadingLogo(true);
                                                    const reader = new FileReader();
                                                    reader.readAsDataURL(file);
                                                    reader.onload = async () => {
                                                        const base64 = reader.result as string;
                                                        // Hiển thị tạm bằng base64 để trải nghiệm mượt mà
                                                        handleChange('logoContentLocation', base64);
                                                        
                                                        const res = await SignatureAssetService.uploadLogo(base64);
                                                        if (res && res.logoContentLocation) {
                                                            handleChange('logoContentLocation', res.logoContentLocation);
                                                        }
                                                        setUploadingLogo(false);
                                                    };
                                                } catch (err) {
                                                    console.error('Error uploading logo:', err);
                                                    setUploadingLogo(false);
                                                }
                                            }
                                        }}
                                        auto
                                        chooseLabel={config.logoContentLocation ? "Đổi Logo" : "Tải Logo lên"}
                                        className="p-button-outlined p-button-sm"
                                    />
                                    {config.logoContentLocation && (
                                        <span className="text-xs text-green-500 font-medium"><i className="pi pi-check-circle mr-1"></i>Đã tải lên</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-6">
                <div className="flex flex-column gap-3 h-full px-2">
                    <h5 className="m-0 text-lg font-semibold">Xem trước</h5>
                    <span className="text-color-secondary text-sm">Hiển thị trực quan nội dung chữ ký sẽ được in trên tài liệu</span>
                    
                    <div className="flex-1 surface-ground border-round-md border-1 surface-border border-dashed flex justify-content-center align-items-center p-4">
                        <div 
                            className="bg-white border-1 surface-border shadow-4 p-4 border-round-md"
                            style={{ 
                                display: 'flex', 
                                flexDirection: config.layoutStyle === 'STYLE_2' ? 'row' : 'column',
                                alignItems: config.layoutStyle === 'STYLE_2' ? 'center' : 'stretch',
                                gap: '20px',
                                minWidth: '450px',
                                maxWidth: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Khối ảnh Chữ ký */}
                            <div className="flex align-items-center justify-content-center" style={{ flexShrink: 0, position: 'relative', minWidth: '220px', minHeight: '110px' }}>
                                {previewImageBase64 ? (
                                    <img 
                                        src={previewImageBase64} 
                                        alt="Signature Preview" 
                                        style={{ 
                                            maxHeight: '150px', 
                                            maxWidth: '300px', 
                                            objectFit: 'contain', 
                                            zIndex: 2, 
                                            position: 'relative' 
                                        }} 
                                    />
                                ) : (
                                    <div className="flex align-items-center justify-content-center border-round text-color-secondary text-sm" style={{ width: '220px', height: '110px', zIndex: 2, position: 'relative', border: '1px dashed var(--surface-300)' }}>
                                        (Ảnh chữ ký)
                                    </div>
                                )}
                                
                                {/* Logo làm mờ (Watermark) - Căn giữa và nằm dưới cùng */}
                                {config.showLogo === 'Y' && config.logoContentLocation && (
                                    <img 
                                        src={getLogoUrl(config.logoContentLocation)} 
                                        alt="Logo Watermark" 
                                        style={{ 
                                            position: 'absolute', 
                                            top: '50%', 
                                            left: '50%', 
                                            transform: 'translate(-50%, -50%)', 
                                            height: '120px', 
                                            width: '120px', 
                                            objectFit: 'contain',
                                            opacity: 0.3,
                                            zIndex: 1,
                                            pointerEvents: 'none'
                                        }} 
                                    />
                                )}
                            </div>
                            
                            {/* Khối chữ */}
                            {(mockedDisplayLines.length > 0) && (
                                <div className="flex flex-column justify-content-center" style={{ fontSize: '13px', lineHeight: '1.5', color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                                    {mockedDisplayLines.map((line, idx) => (
                                        <div key={idx} className={idx === 0 ? "font-bold" : ""}>{line}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
